import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

const MAX_CANVAS_DIMENSION = 8192;

/**
 * Re-encode raster images as PNG before upload. Some gateways/WAFs mis-handle JPEG/WebP multipart
 * bodies and return 403; PNG multipart is more consistent. SVG/unsupported types fall back to original file.
 */
function normalizeImageFileToPngBlob(file) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith("image/") || file.type === "image/svg+xml") {
      resolve(file);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (!w || !h) {
          URL.revokeObjectURL(url);
          resolve(file);
          return;
        }
        if (w > MAX_CANVAS_DIMENSION || h > MAX_CANVAS_DIMENSION) {
          const scale = Math.min(MAX_CANVAS_DIMENSION / w, MAX_CANVAS_DIMENSION / h);
          w = Math.floor(w * scale);
          h = Math.floor(h * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) {
              resolve(file);
              return;
            }
            const base =
              (file.name && file.name.replace(/\.[^.]+$/, "")) || "offer_promotion";
            const safeName = `${base.replace(/[^a-zA-Z0-9_-]/g, "_")}.png`;
            resolve(new File([blob], safeName, { type: "image/png" }));
          },
          "image/png",
          0.92
        );
      } catch {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}

export const saveOfferAndPromotions = async (imageFile, videoFile, caption, videoType, videoUrl) => {
  const formData = new FormData();

  if (imageFile) {
    const imageForUpload = await normalizeImageFileToPngBlob(imageFile);
    formData.append("image", imageForUpload);
  } else {
    formData.append("image", new Blob(), "empty.jpg"); // backend requires image, send empty if not used
  }

  if (videoFile) {
    formData.append("video", videoFile);
  } else {
    formData.append("video", new Blob(), "empty.mp4"); // backend requires video, send empty if not used
  }

  const offerAndPromotionsData = {
    offerAndPromotionCaption: caption,
    offerAndPromotionsVideoType: videoType || "",
    offerAndPromotionsVideoUrl: videoUrl || ""
  };

  formData.append("offerAndPromotionsData", JSON.stringify(offerAndPromotionsData));

  // Add Authorization header for authenticated requests.
  // Prefer MSIL ID token, then MSIL access token, then MASS session token.
  const token =
    localStorage.getItem("msil_id_token") ||
    localStorage.getItem("msil_access_token") ||
    sessionStorage.getItem("msil_id_token") ||
    sessionStorage.getItem("token");
  
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  // Note: Don't set Content-Type for multipart/form-data - let axios set it automatically with boundary

  const response = await axios.post(`${apiUrl}/api/v1/offerAndPromotions/saveOfferAndPromotions`, formData, {
    headers: headers,
    withCredentials: true,
  });

  return response.data;
};
